<template>
  <div class="relative flex items-center mb-3">
    <NotebookPen class="w-6 h-6 mx-2 text-gray-500 pointer-events-none" aria-hidden="true" />
    <span class="dark:text-white font-bold">{{ $utils.getRecordName(company) }}</span>
    <div class="absolute top-0 right-0 flex flex-wrap justify-end gap-2 px-2 pb-2">
      <div class="relative flex items-center w-full rounded-full whitespace-nowrap hover:cursor-pointer group">
        <X
          class="flex-shrink-0 w-6 h-6 text-gray-500 rounded-full group-hover:bg-gray-100 dark:group-hover:bg-dark-screen-background"
          @click="$emit('cancel')" />
      </div>
    </div>
  </div>
  <div
    class="px-2 min-h-[250px] max-h-[500px] h-[500px] overflow-y-auto relative w-full dark:bg-dark-box-background"
    :class="[fetched_all_notes && notes.length == 0 ? '' : 'overflow-y-auto']">
    <div class="flex items-center justify-between">
      <div class="flex items-center truncate overflow-ellipsis">
        <div
          v-if="is_add_new_note || is_edit_note"
          class="mr-1"
          @click="
            () => {
              is_add_new_note = false
              is_edit_note = false
              note = { title: null, description: null, categories: [] }
            }
          ">
          <ChevronLeft class="w-5 h-5 cursor-pointer" aria-hidden="true" />
        </div>
        <div v-if="notes.length > 0 && fetched_all_notes && !is_add_new_note && !is_edit_note" class="flex flex-col w-32">
          <div
            class="flex items-center justify-between h-8 px-4 py-2 border-2 rounded-full shadow-lg cursor-pointer max-h-8 dark:bg-dark-screen-background group"
            :style="{
              background: `${selected_filters.length == 0 || selected_filters.length >= 2 ? '#202B31' : `${selected_filters[0].color}`}`
            }"
            @click="
              () => {
                filter_is_open = !filter_is_open
              }
            ">
            <div class="text-sm font-bold text-white capitalize truncate dark:text-dark-body-text">
              {{ selected_filters.length > 1 ? "See filter" : selected_filters.length == 1 ? selected_filters[0].name : "Show all" }}
            </div>
            <ChevronDown class="w-5 h-5 text-white" aria-hidden="true" />
          </div>
          <span class="text-[13px] text-common-grey">{{
            selected_filters.length == 0
              ? $t("Showing all") + ` ${notes.length} ` + $t("notes")
              : $t("Showing") +
                ` ${notes.filter((n) => n.categories?.some((r) => selected_filters.map((f) => f.id).indexOf(r) >= 0)).length} ` +
                $t("of") +
                ` ${notes.length} ` +
                $t("notes")
          }}</span>
          <div v-if="filter_is_open" class="absolute z-10 w-32 bg-white top-9">
            <div
              class="flex items-center justify-center px-4 mb-1 text-sm font-bold text-white capitalize border-2 rounded-full shadow-lg cursor-pointer dark:text-dark-body-text dark:bg-dark-screen-background group"
              :style="{
                background: `${selected_filters.length == 0 ? '#202B31' : '#FFFFFF'}`,
                color: `${selected_filters.length == 0 ? '#FFFFFF' : '#000000'}`,
                borderColor: '#000000'
              }"
              @click.stop="
                () => {
                  filter_is_open = false
                  selected_filters = []
                }
              ">
              {{ $t("All") }}
            </div>
            <div
              v-for="filter in all_filters"
              :key="filter.id"
              class="flex items-center justify-center px-4 mb-1 text-sm font-bold text-white capitalize border-2 rounded-full shadow-lg cursor-pointer dark:text-dark-body-text dark:bg-dark-screen-background group"
              :style="{
                background: `${selected_filters.find((f) => f.id == filter.id) ? filter.color : '#FFFFFF'}`,
                color: `${selected_filters.find((f) => f.id == filter.id) ? '#FFFFFF' : filter.color}`,
                borderColor: `${filter.color}`
              }"
              @click.stop="handleFilter(filter)">
              {{ filter.name }}
            </div>
          </div>
        </div>
      </div>
      <button type="button" class="primary-btn w-fit" @click="createUpdateNote()">
        <span class="text-[14px] not-italic font-normal font-sans leading-5">{{
          is_add_new_note || is_edit_note ? "Save" : "+ Add new note"
        }}</span>
      </button>
    </div>

    <div v-if="!is_add_new_note && !is_edit_note" class="h-[calc(100%-42px)]">
      <div v-if="!fetched_all_notes && !is_add_new_note && !is_edit_note" class="text-common-grey">
        {{ $t("Loading...") }}
      </div>
      <div
        v-if="fetched_all_notes && notes.length == 0 && !is_add_new_note && !is_edit_note"
        class="w-full h-full -mt-6 flex items-center justify-center text-[14px] not-italic font-normal font-sans leading-5">
        {{ $t("You have no notes added") }}
      </div>
      <div
        v-if="fetched_all_notes && !is_add_new_note && !is_edit_note"
        class="relative w-full h-[calc(100%-66px)] mb-2 mt-2"
        :class="[{ 'pointer-events-none': loading }]">
        <div
          v-for="note in selected_filters.length == 0
            ? notes
            : notes.filter((n) => n.categories?.some((r) => selected_filters.map((f) => f.id).indexOf(r) >= 0))"
          :key="note.id"
          class="relative flex flex-col mb-2 border rounded-lg">
          <div
            v-show="fetched_categories"
            class="absolute right-0 h-full w-2 rounded-r-lg"
            :style="{
              background: `${initial_categories.find((cat) => note.categories?.includes(cat.id))?.color}`
            }" />

          <div class="py-2 pl-2 pr-4">
            <div class="flex justify-between">
              <div class="has-tooltip">
                <img
                  v-if="$utils.getDomainName(company?.values[$utils.getOptionMetricReference('company', 'website')])"
                  class="inline-block object-contain w-6 rounded-lg"
                  :src="`https://logosnap.io/api/${$utils.getDomainName(
                    company?.values[$utils.getOptionMetricReference('company', 'website')]
                  )}?token=ecoTB81m5dQqkrjw`"
                  :alt="company?.name">
                <Avatar
                  v-else
                  :hover-effect="true"
                  :first-name="company?.name ? company?.name : Record.from(company).info?.name"
                  :last-name="null" />

                <span class="flex-wrap px-1 text-sm text-white bg-gray-700 rounded tooltip whitespace-nowrap">{{ company?.name }}</span>
              </div>
              <div v-show="fetched_categories" class="flex items-center gap-1 overflow-x-scroll">
                <div
                  v-for="category in note?.is_all_assigned_categories_shown ? note.categories : note.categories.slice(0, 3)"
                  :key="category"
                  class="flex items-center justify-center px-1 rounded-lg"
                  :style="{
                    background: `${initial_categories.find((cat) => category.includes(cat.id))?.color}`
                  }">
                  <span class="truncate text-[12px] font-bold text-white">{{
                    initial_categories.find((cat) => category.includes(cat.id))?.name
                  }}</span>
                </div>
                <div
                  v-if="note.categories.length > 3 && !note?.is_all_assigned_categories_shown"
                  class="flex items-center justify-center rounded-lg px-1 bg-[#202B31] cursor-pointer"
                  @click="note['is_all_assigned_categories_shown'] = true">
                  <span class="text-[12px] font-bold text-white">+{{ note.categories.length - 3 }} {{ $t("more") }}</span>
                </div>
                <div
                  v-if="note?.is_all_assigned_categories_shown"
                  class="flex items-center justify-center rounded-lg px-1 bg-[#202B31] cursor-pointer"
                  @click="note['is_all_assigned_categories_shown'] = false">
                  <span class="text-[12px] font-bold text-white flex whitespace-nowrap">{{ $t("Show less") }}</span>
                </div>
              </div>
            </div>
            <div
              class="h-[20px] max-h-[20px] min-h-[20px] truncate overflow-ellipsis text-[14px] not-italic font-medium font-sans leading-normal">
              {{ note.title }}
            </div>
            <TextEditor
              :combined-values-listner="note.description"
              class="-my-2 h-[38px] max-h-[38px] min-h-[38px] truncate overflow-ellipsis relative"
              input-classes="-ml-3"
              :transparent-bg="true"
              :disabled="true"
              :no-border="true"
              :value="$utils.extractValue(note.description, 'N/A', $route)"
              :name="`description`"
              label="" />
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="bg-white rounded-l-lg cursor-pointer" @click="editNote(note)">
                  <FilePenLine v-if="!editing_disabled" class="relative text-common-grey hover:text-gray-700 w-5 h-5" />
                </div>
                <Trash2
                  v-if="!is_confirmation_delete_open && !editing_disabled"
                  class="w-5 h-5 ml-1 cursor-pointer text-common-grey hover:text-red-500"
                  @click.prevent="
                    () => {
                      note_to_be_deleted = note
                      is_confirmation_delete_open = true
                    }
                  " />
              </div>
              <div class="flex flex-col items-end text-[12px] not-italic font-normal font-sans leading-normal text-common-grey">
                <span>{{ $utils.getUserById(note.created_by, "full_name") }}</span>
                <span>{{ toDate(note.last_edited) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col relative w-full h-[calc(100%-46px)] mt-1 gap-2">
      <div class="mb-1 flex justify-between w-full gap-2 items-stretch">
        <TextInput
          :value="note.title"
          label=""
          :show-label="false"
          :classes="'w-full'"
          name="name"
          type="text"
          :placeholder="$t('Title')"
          @update:modelValue="
            (e) => {
              note.title = e
            }
          " />
        <MultiSelectInput
          v-model:current-items="note.categories"
          :value="note.categories"
          :disabled="false"
          name="Categories"
          :items="initial_categories"
          label-attr="name"
          value-attr="id"
          :allow-null="true"
          :label="$t('Categories')"
          classes=""
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
          :placeholder="$t('Categories')"
          :show-label="false" />
      </div>
      <div class="relative w-full rounded-md">
        <TextEditor
          :value="note.description"
          :show-label="false"
          :no-border="true"
          toolbar="essential"
          name="note"
          :label="$t('Note')"
          theme="snow"
          input-classes="w-full rounded-b-md"
          :placeholder="$t('Description')"
          @changed="
            (e) => {
              note.description = formatLink(e)
            }
          " />
      </div>
    </div>

    <!-- <div class="mt-3 mbn-2" v-html="note.description" /> -->

    <!-- Delete Note -->
    <Modal
      v-model:is-open="is_confirmation_delete_open"
      max-width="max-w-[600px] mx-5"
      :use-toast="true"
      :default-success-msg="$t('Note deleted successfully')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Note") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete note, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteNote(showIsDone)">
            {{ $t("Delete Note") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import TextEditor from "@/components/shared/TextEditor.vue"
import dataService from "@/services/data.service"
import { NotebookPen, FilePenLine, ChevronDown, X, Trash2, ChevronLeft } from "lucide-vue-next"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import Modal from "@/components/shared/Modal.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

import Avatar from "@/components/shared/Avatar.vue"
import { Record } from "@/models/record"

export default {
  components: {
    Modal,
    Avatar,
    TextInput,
    FilePenLine,
    TextEditor,
    MultiSelectInput,
    NotebookPen,
    X,
    Trash2,
    ChevronLeft,
    ChevronDown
  },
  inject: ["canUserEditLayout"],
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel"],
  data() {
    return {
      Record,
      filter_is_open: false,
      fetched_categories: false,
      is_add_new_note: false,
      is_edit_note: false,
      is_confirmation_delete_open: false,
      note: { id: null, title: null, description: null, categories: [] },
      note_to_be_deleted: null,
      notes: [],
      fetched_all_notes: false,
      loading: false,
      initial_categories: [],
      all_filters: [],
      selected_filters: []
    }
  },

  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },

  created() {
    this.getFilteredCategories()
    this.init()
  },
  activated() {
    this.init()
  },

  methods: {
    init() {
      dataService.getRecordNotes(this.company.id).then((result) => {
        this.notes = result.data.results
        this.fetched_all_notes = true
      })
    },
    getFilteredCategories() {
      this.initial_categories = this.$store.state.data.note_categories
      this.fetched_categories = true
      this.all_filters = this.$utils.clone(this.initial_categories)
    },

    addNewNote() {
      let index = this.notes.findIndex((note) => note.id == this.note?.id)
      if (index != -1) {
        //Update existing note
        dataService
          .updateRecordNote(this.company.id, this.note.id, {
            title: this.note.title,
            description: this.note.description,
            categories: this.note.categories
          })
          .then((res) => {
            this.notes[index] = res.data
            this.note = { title: null, description: null, categories: [] }
            this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, {
              id: this.company.id,
              count: this.notes.length
            })
          })
      } else {
        //Create new note
        dataService
          .addNoteToRecord(this.company.id, {
            title: this.note.title,
            description: this.note.description,
            categories: this.note.categories
          })
          .then((res) => {
            this.notes.unshift(res.data)
            this.note = { title: null, description: null, categories: [] }
            this.$emitter.emit(ALL_EMITTERS.NEW_NOTE_ADDED, {
              id: this.company.id,
              note: res.data
            })
            this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, {
              id: this.company.id,
              count: this.notes.length
            })
          })
      }
    },

    editNote(note) {
      this.is_edit_note = true
      this.note = note
    },

    confirmDeleteNote(showIsDone) {
      dataService.deleteRecordNote(this.company.id, this.note_to_be_deleted.id).then(() => {
        let index = this.notes.findIndex((note) => note.id == this.note_to_be_deleted.id)
        this.notes.splice(index, 1)
        showIsDone()
        this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, {
          id: this.company.id,
          count: this.notes.length
        })
      })
    },

    createUpdateNote() {
      if (this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = false
        this.addNewNote()
      } else if (!this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = true
      } else if (this.is_edit_note) {
        this.is_edit_note = false
        this.addNewNote()
      }
    },

    handleFilter(filter) {
      this.filter_is_open = !this.filter_is_open
      let index = this.selected_filters.findIndex((f) => f.id == filter.id)
      if (index != -1) {
        this.selected_filters.splice(index, 1)
      } else {
        this.selected_filters.push(filter)
      }
    },

    toDate(date_string) {
      let date = new Date(date_string)
      const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" }
      let localStringDate = date.toLocaleDateString(undefined, options)
      return localStringDate.substr(localStringDate.indexOf(",") + 1)
    },

    formatLink(str) {
      return str.replace(/href="(?!http)/, 'href="http://')
    }
  }
}
</script>
